import api from '../../../services/api';
import { 
  ApiResponse, 
  User, 
  Parish, 
  Event, 
  Transaction, 
  DashboardSummary,
  GrowthMetrics,
  PlatformConfig,
  AuditLog,
  ParishFilters,
  EventFilters,
  TransactionFilters
} from '../../../types/types';

// Auth services
export const authService = {
  login: async (email: string, password: string): Promise<ApiResponse<{ admin: User; token: string }>> => {
    const response = await api.post('/admin/auth/login', { email, password });
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/admin/auth/logout');
  },

  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    console.log('Cai aqui admin/auth/me')
    const response = await api.get('/admin/auth/me');
    return response.data;
  },
};

// Dashboard services
export const dashboardService = {
  getSummary: async (): Promise<ApiResponse<{ summary: DashboardSummary; growth: GrowthMetrics }>> => {
    const response = await api.get('/admin/dashboard/summary');
    return response.data;
  },
};

// Parish services
export const parishService = {
  getAll: async (filters: ParishFilters = {}): Promise<ApiResponse<{ parishes: Parish[]; pagination: any }>> => {
    const response = await api.get('/admin/parishes', { params: filters });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Parish>> => {
    const response = await api.get(`/admin/parishes/${id}`);
    return response.data;
  },

  updateStatus: async (id: string, status: string, rejectionReason?: string): Promise<ApiResponse<Parish>> => {
    const response = await api.put(`/admin/parishes/${id}/status`, { status, rejectionReason });
    return response.data;
  },

  update: async (id: string, data: Partial<Parish>): Promise<ApiResponse<Parish>> => {
    const response = await api.put(`/admin/parishes/${id}`, data);
    return response.data;
  },

  /** Iguala `Parish.email` ao email de login do parish_admin principal (corrige divergências antigas). */
  alignEmailWithPrimaryAdmin: async (
    id: string
  ): Promise<ApiResponse<{ sync: Record<string, unknown>; parish: Parish }>> => {
    const response = await api.post(`/admin/parishes/${id}/align-email-with-primary-admin`);
    return response.data;
  },

  alignPrimaryAdminEmailWithParish: async (
    id: string
  ): Promise<ApiResponse<{ sync: Record<string, unknown>; parish: Parish }>> => {
    const response = await api.post(`/admin/parishes/${id}/align-primary-admin-email-with-parish`);
    return response.data;
  },

  verifyPrimaryParishAdminEmail: async (
    id: string
  ): Promise<ApiResponse<{ parishAdmin: Record<string, unknown> | null }>> => {
    const response = await api.post(`/admin/parishes/${id}/verify-primary-admin-email`);
    return response.data;
  },

  updateParishAdminWithdrawalAccess: async (
    userId: string,
    allowWithdrawalAccess: boolean
  ): Promise<ApiResponse<{ _id: string; allowWithdrawalAccess: boolean }>> => {
    const response = await api.put(`/admin/parish-users/${userId}/withdrawal-access`, {
      allowWithdrawalAccess,
    });
    return response.data;
  },

  updateParishAdminAdditionalOrganizations: async (
    userId: string,
    allowAdditionalOrganizations: boolean
  ): Promise<ApiResponse<{ _id: string; allowAdditionalOrganizations: boolean }>> => {
    const response = await api.put(`/admin/parish-users/${userId}/additional-organizations`, {
      allowAdditionalOrganizations,
    });
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/admin/parishes/stats');
    return response.data;
  },

  getPagarmeOnboardingPending: async (
    view:
      | 'pending'
      | 'ready'
      | 'error'
      | 'draft'
      | 'pending_sync'
      | 'provider_refused' = 'pending'
  ): Promise<
    ApiResponse<{
      parishes: Array<Record<string, unknown>>;
      total: number;
      view?: string;
    }>
  > => {
    const response = await api.get('/admin/parishes/pagarme-onboarding/pending', {
      params: { view },
    });
    return response.data;
  },

  getPagarmeSplitOverview: async (): Promise<
    ApiResponse<{
      items: Array<{
        _id: string;
        name: string;
        city?: string;
        state?: string;
        cpfCnpj?: string | null;
        cpfCnpjType?: 'cpf' | 'cnpj' | null;
        status: string;
        active: boolean;
        pagarMeRecipientId?: string | null;
        hasSplitRecipient: boolean;
        onboardingStatus: string;
        recipientSynced: boolean;
        lastSyncError?: string | null;
        providerStatus?: string | null;
        providerStatusReason?: string | null;
        providerStatusCheckedAt?: string | null;
      }>;
      total: number;
    }>
  > => {
    const response = await api.get('/admin/parishes/pagarme-split-overview');
    return response.data;
  },

  getPagarmeRecipientStatus: async (
    parishId: string
  ): Promise<
    ApiResponse<{
      parishId: string;
      parishName: string;
      recipientId: string;
      localOnboardingStatus: string;
      providerStatus?: string | null;
      providerReason?: string | null;
      checkedAt: string;
    }>
  > => {
    const response = await api.get(`/admin/parishes/${parishId}/pagarme-recipient-status`);
    return response.data;
  },

  /** Superadmin: validar (dryRun) ou criar/atualizar recebedor no Pagar.me. */
  adminSyncPagarmeRecipient: async (
    parishId: string,
    opts?: { dryRun?: boolean }
  ): Promise<
    ApiResponse<{
      pendencies?: string[];
      recipientProfile?: Record<string, unknown>;
      pagarMeRecipientId?: string | null;
    }>
  > => {
    const response = await api.post(`/admin/parishes/${parishId}/pagarme-recipient/sync`, {
      dryRun: !!opts?.dryRun,
    });
    return response.data;
  },

  sendPagarmeOnboardingEmail: async (parishId: string): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await api.post(`/admin/parishes/${parishId}/pagarme-onboarding/send-email`);
    return response.data;
  },

  issuePagarmePublicOnboardingLink: async (
    parishId: string
  ): Promise<ApiResponse<{ url: string; expiresAt: string }>> => {
    const response = await api.post(`/admin/parishes/${parishId}/pagarme-onboarding/public-link`);
    return response.data;
  },

  getPagarmeOnboardingReview: async (
    parishId: string
  ): Promise<
    ApiResponse<{
      parish: Parish & Record<string, unknown>;
      pendencies: string[];
      recipientSynced: boolean;
      pixHint?: string | null;
    }>
  > => {
    const response = await api.get(`/admin/parishes/${parishId}/onboarding-pagarme`);
    return response.data;
  },

  patchPagarmeOnboardingReview: async (
    parishId: string,
    body: Record<string, unknown>
  ): Promise<
    ApiResponse<{
      parish: Parish & Record<string, unknown>;
      pendencies: string[];
      recipientSynced: boolean;
    }>
  > => {
    const response = await api.patch(`/admin/parishes/${parishId}/onboarding-pagarme`, body);
    return response.data;
  },
};

// Withdrawal requests (admin)
export interface AdminWithdrawalRequest {
  id: string;
  eventId: string;
  eventTitle: string;
  eventSlug: string;
  parishId: string;
  parishName: string;
  parishCity: string;
  parishState: string;
  parishEmail?: string;
  pixKey?: string;
  pixKeyType?: string;
  amount: number;
  status: string;
  requestedBy: string;
  requestedAt: string;
  completedAt?: string;
  rejectionReason?: string;
}

/** Eventos com saldo disponível que ainda não solicitaram saque */
export interface PotentialWithdrawal {
  eventId: string;
  eventTitle: string;
  eventSlug: string;
  parishId: string;
  parishName: string;
  parishCity: string;
  parishState: string;
  totalParishFee: number;
  alreadyWithdrawn: number;
  availableBalance: number;
  totalRegistrations: number;
  /** Líquido paróquia só de inscrições Mercado Pago pagas com cartão. */
  mercadopagoCardParishNet: number;
  /** Estimativa após alocar saques já feitos proporcionalmente ao mix do evento. */
  mercadopagoCardAvailableReais: number;
  /** Saldo disponível na conta recebedora Pagar.me da paróquia (API); null se indisponível. */
  pagarmeRecipientBalanceReais: number | null;
  pagarmeRecipientBalanceCode?: 'no_recipient' | 'fetch_failed';
  pagarmeParishNet: number;
  mercadopagoPixParishNet: number;
  mercadopagoAmbiguousParishNet: number;
  reconciliationOk?: boolean;
  reconciliationMessages?: string[];
  /** Visão admin: legado (MP), Pagarme (split) ou misto. */
  settlementView?: 'pagarme' | 'legacy' | 'mixed';
  pagarmePixCheckoutEnabled?: boolean | null;
  pagarmeCheckoutReady?: boolean;
}

export interface WithdrawalSafetyStats {
  countsByStatus: Record<string, number>;
  pendingOldCount: number;
  processingStuck: Array<{
    requestId: string;
    eventId?: string;
    parishId?: string;
    amount: number;
    requestedAt?: string;
    processingAt?: string;
  }>;
  expiredUserTokens: number;
  expiredAdminTokens: number;
  thresholds: { pendingDays: number; processingDays: number };
}

export interface WithdrawalGlobalAdminState {
  enabled: boolean;
  suspended: boolean;
  suspendedMessage: string;
  disabledMessage: string;
}

export interface LedgerEntryLine {
  accountKey: string;
  debit: number;
  credit: number;
  currency: 'BRL';
}

export interface LedgerEntryItem {
  _id: string;
  txKey: string;
  kind: string;
  status: 'posted' | 'reversed';
  occurredAt: string;
  provider?: string;
  providerPaymentId?: string;
  parishId?: string;
  eventId?: string;
  note?: string;
  lines: LedgerEntryLine[];
}

export interface LedgerSummary {
  period: { from: string; to: string };
  totalEntries: number;
  byProvider: Record<string, number>;
  platformRevenue: number;
  pendingRecon: number;
}

export interface ReconciliationRun {
  _id: string;
  provider: string;
  status: 'running' | 'completed' | 'failed';
  startDate: string;
  endDate: string;
  summary: {
    matched: number;
    missingInternal: number;
    missingProvider: number;
    amountMismatch: number;
    lateSettlement: number;
  };
  createdAt: string;
}

export interface ReconciliationItem {
  _id: string;
  runId: string;
  status: string;
  txKey?: string;
  provider?: string;
  providerPaymentId?: string;
  expectedAmount?: number;
  providerAmount?: number;
  details?: string;
}

export const withdrawalRequestService = {
  getPotential: async (): Promise<ApiResponse<PotentialWithdrawal[]>> => {
    const response = await api.get('/admin/withdrawal-requests/potential');
    return response.data;
  },
  markAsPaid: async (eventId: string, amount: number): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/admin/withdrawal-requests/mark-as-paid', { eventId, amount });
    return response.data;
  },
  correctManual: async (
    requestId: string,
    amount: number
  ): Promise<ApiResponse<{ message: string; data?: { id: string; amount: number; previousAmount: number } }>> => {
    const response = await api.patch(`/admin/withdrawal-requests/${requestId}/correct-manual`, { amount });
    return response.data;
  },
  deleteManual: async (
    requestId: string
  ): Promise<ApiResponse<{ message: string; data?: { id: string; deletedAmount: number } }>> => {
    const response = await api.delete(`/admin/withdrawal-requests/${requestId}/manual`);
    return response.data;
  },
  getMercadoPagoBalance: async (): Promise<
    ApiResponse<{
      userId: number;
      availableBalance: number;
      totalAmount: number;
      unavailableBalance: number;
      currencyId: string;
      fetchedAt: string;
      source: 'api' | 'internal_estimate';
      apiNote?: string;
    }>
  > => {
    const response = await api.get('/admin/mercadopago/balance');
    return response.data;
  },
  getAll: async (status?: string): Promise<ApiResponse<AdminWithdrawalRequest[]>> => {
    const response = await api.get('/admin/withdrawal-requests', {
      params: status ? { status } : {}, // ex: "pending,processing" | "completed" | "rejected,cancelled"
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    return response.data;
  },
  sendApprovalToken: async (requestId: string): Promise<ApiResponse<null>> => {
    const response = await api.post(`/admin/withdrawal-requests/${requestId}/send-approval-token`);
    return response.data;
  },
  approve: async (requestId: string, receipt: File): Promise<ApiResponse<null>> => {
    const formData = new FormData();
    formData.append('receipt', receipt);
    const response = await api.put(`/admin/withdrawal-requests/${requestId}/approve`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  reject: async (requestId: string, reason?: string): Promise<ApiResponse<null>> => {
    const response = await api.put(`/admin/withdrawal-requests/${requestId}/reject`, { reason });
    return response.data;
  },
  getSafetyStats: async (params?: {
    pendingDays?: number;
    processingDays?: number;
    limit?: number;
  }): Promise<ApiResponse<WithdrawalSafetyStats>> => {
    const response = await api.get('/admin/withdrawal-requests/safety-stats', { params });
    return response.data;
  },
  getWithdrawalGlobalControl: async (): Promise<ApiResponse<WithdrawalGlobalAdminState>> => {
    const response = await api.get('/admin/withdrawals/state');
    return response.data;
  },
  setWithdrawalGlobalControl: async (params: {
    mode: 'active' | 'suspended' | 'disabled';
    suspendedMessage: string;
    disabledMessage: string;
  }): Promise<ApiResponse<WithdrawalGlobalAdminState>> => {
    const response = await api.put('/admin/withdrawals/state', params);
    return response.data;
  },
};

export const ledgerService = {
  getSummary: async (params?: { from?: string; to?: string }): Promise<ApiResponse<LedgerSummary>> => {
    const response = await api.get('/admin/ledger/summary', { params });
    return response.data;
  },
  getEntries: async (params?: Record<string, unknown>): Promise<ApiResponse<{ items: LedgerEntryItem[]; total: number; page: number; limit: number }>> => {
    const response = await api.get('/admin/ledger/entries', { params });
    return response.data;
  },
  getEntry: async (id: string): Promise<ApiResponse<LedgerEntryItem>> => {
    const response = await api.get(`/admin/ledger/entries/${id}`);
    return response.data;
  },
  getReconciliationRuns: async (): Promise<ApiResponse<ReconciliationRun[]>> => {
    const response = await api.get('/admin/ledger/reconciliation/runs');
    return response.data;
  },
  getReconciliationItems: async (runId: string): Promise<ApiResponse<ReconciliationItem[]>> => {
    const response = await api.get(`/admin/ledger/reconciliation/runs/${runId}/items`);
    return response.data;
  },
  runReconciliation: async (body: { provider: 'mercadopago' | 'pagarme' | 'all'; startDate?: string; endDate?: string }): Promise<ApiResponse<ReconciliationRun>> => {
    const response = await api.post('/admin/ledger/reconciliation/run', body);
    return response.data;
  },
  createAdjustment: async (body: {
    reason: string;
    parishId?: string;
    eventId?: string;
    lines: LedgerEntryLine[];
  }): Promise<ApiResponse<LedgerEntryItem>> => {
    const response = await api.post('/admin/ledger/adjustments', body);
    return response.data;
  },
};

// Event services
export const eventService = {
  getAll: async (filters: EventFilters = {}): Promise<ApiResponse<{ events: Event[]; pagination: any }>> => {
    const response = await api.get('/admin/events', { params: filters });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Event>> => {
    const response = await api.get(`/admin/events/${id}`);
    return response.data;
  },

  updateStatus: async (id: string, status: string, reason?: string): Promise<ApiResponse<Event>> => {
    const response = await api.put(`/admin/events/${id}/status`, { status, reason });
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/admin/events/stats');
    return response.data;
  },

  getUpcoming: async (limit?: number): Promise<ApiResponse<Event[]>> => {
    const response = await api.get('/admin/events/upcoming', { params: { limit } });
    return response.data;
  },

  getByParish: async (parishId: string, filters: any = {}): Promise<ApiResponse<{ events: Event[]; pagination: any }>> => {
    const response = await api.get(`/admin/parishes/${parishId}/events`, { params: filters });
    return response.data;
  },

  approve: async (id: string): Promise<ApiResponse<Event>> => {
    console.log('🔍 [EVENT SERVICE] Aprovando evento:', id);
    console.log('🔍 [EVENT SERVICE] URL:', `/admin/events/${id}/approve`);
    console.log('🔍 [EVENT SERVICE] Token atual:', localStorage.getItem('eventos_catolicos_admin_token'));
    
    const response = await api.put(`/admin/events/${id}/approve`);
    console.log('✅ [EVENT SERVICE] Resposta da aprovação:', response.data);
    return response.data;
  },

  reject: async (id: string, reason?: string): Promise<ApiResponse<Event>> => {
    console.log('🔍 [EVENT SERVICE] Rejeitando evento:', id, 'Motivo:', reason);
    console.log('🔍 [EVENT SERVICE] URL:', `/admin/events/${id}/reject`);
    console.log('🔍 [EVENT SERVICE] Token atual:', localStorage.getItem('eventos_catolicos_admin_token'));
    
    const response = await api.put(`/admin/events/${id}/reject`, { reason });
    console.log('✅ [EVENT SERVICE] Resposta da rejeição:', response.data);
    return response.data;
  },

  update: async (id: string, data: FormData): Promise<ApiResponse<Event>> => {
    const response = await api.put(`/admin/events/${id}/update`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  create: async (data: FormData): Promise<ApiResponse<Event>> => {
    const response = await api.post('/admin/events/create', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getRegistrations: async (id: string, filters: any = {}): Promise<ApiResponse<any>> => {
    const response = await api.get(`/admin/events/${id}/registrations`, { params: filters });
    return response.data;
  },
};

// Transaction services
export const transactionService = {
  getAll: async (filters: TransactionFilters = {}): Promise<ApiResponse<{ transactions: Transaction[]; pagination: any }>> => {
    const response = await api.get('/admin/transactions', { params: filters });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Transaction>> => {
    const response = await api.get(`/admin/transactions/${id}`);
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/admin/transactions/stats');
    return response.data;
  },

  getRevenueByParish: async (parishId?: string, startDate?: string, endDate?: string): Promise<ApiResponse<any[]>> => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const url = parishId ? `/admin/revenue/parishes/${parishId}` : '/admin/revenue/parishes';
    const response = await api.get(url, { params });
    return response.data;
  },

  processRefund: async (id: string, reason: string, amount?: number): Promise<ApiResponse<Transaction>> => {
    const response = await api.post(`/admin/transactions/${id}/refund`, { reason, amount });
    return response.data;
  },

  syncRefunded: async (): Promise<ApiResponse<{ totalChecked: number; updated: number; errors?: string[] }>> => {
    const response = await api.post('/admin/transactions/sync-refunded');
    return response.data;
  },

  listRefundRequests: async (
    status: string = 'pending'
  ): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/admin/refund-requests', { params: { status } });
    return response.data;
  },

  approveRefundRequest: async (
    requestId: string,
    reason?: string
  ): Promise<ApiResponse<any>> => {
    const response = await api.post(`/admin/refund-requests/${requestId}/approve`, {
      ...(reason ? { reason } : {}),
    });
    return response.data;
  },

  rejectRefundRequest: async (
    requestId: string,
    reason?: string
  ): Promise<ApiResponse<any>> => {
    const response = await api.post(`/admin/refund-requests/${requestId}/reject`, {
      ...(reason ? { reason } : {}),
    });
    return response.data;
  },

  listCancellationTickets: async (
    status: 'opened' | 'handled' | 'all' = 'opened'
  ): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/admin/cancellation-tickets', { params: { status } });
    return response.data;
  },

  handleCancellationTicket: async (
    ticketId: string,
    notes?: string
  ): Promise<ApiResponse<any>> => {
    const response = await api.post(`/admin/cancellation-tickets/${ticketId}/handle`, {
      ...(notes ? { notes } : {}),
    });
    return response.data;
  },
};

// Config services
export const configService = {
  getAll: async (category?: string): Promise<ApiResponse<Record<string, PlatformConfig[]>>> => {
    const response = await api.get('/admin/config', { params: { category } });
    return response.data;
  },

  getByKey: async (key: string): Promise<ApiResponse<PlatformConfig>> => {
    const response = await api.get(`/admin/config/${key}`);
    return response.data;
  },

  update: async (key: string, value: any, description?: string): Promise<ApiResponse<PlatformConfig>> => {
    const response = await api.put(`/admin/config/${key}`, { value, description });
    return response.data;
  },

  create: async (config: Omit<PlatformConfig, 'id' | 'updatedBy' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<PlatformConfig>> => {
    const response = await api.post('/admin/config', config);
    return response.data;
  },

  delete: async (key: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/admin/config/${key}`);
    return response.data;
  },

  initializeDefaults: async (): Promise<ApiResponse<any>> => {
    const response = await api.post('/admin/config/initialize');
    return response.data;
  },
};

// Report services
export const reportService = {
  getRevenue: async (filters: any = {}): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/admin/reports/revenue', { params: filters });
    return response.data;
  },

  getParishPerformance: async (filters: any = {}): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/admin/reports/parish-performance', { params: filters });
    return response.data;
  },

  getEventPerformance: async (filters: any = {}): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/admin/reports/event-performance', { params: filters });
    return response.data;
  },

  getGeographic: async (filters: any = {}): Promise<ApiResponse<any>> => {
    const response = await api.get('/admin/reports/geographic', { params: filters });
    return response.data;
  },

  export: async (type: string, format: string = 'json', filters: any = {}): Promise<any> => {
    const response = await api.get('/admin/reports/export', { 
      params: { type, format, ...filters },
      responseType: format === 'json' ? 'json' : 'blob'
    });
    return response.data;
  },
};

// =============================================================================
// Email delivery report
// =============================================================================
export interface EmailDeliveryKpis {
  totalPaid: number;
  withEmailSuccess: number;
  withEmailFailureOnly: number;
  withoutEmail: number;
}

export interface EmailDeliveryPerEvent {
  eventId: string;
  eventTitle?: string;
  eventStartDate?: string;
  totalPaid: number;
  withEmailSuccess: number;
  withEmailFailureOnly: number;
  withoutEmail: number;
}

export interface EmailDeliveryMissingItem {
  registrationId: string;
  createdAt: string;
  confirmationCode: string;
  amountPaid?: number;
  paymentMethod?: string;
  participantEmail?: string;
  participantName?: string;
  eventId: string | null;
  eventTitle?: string;
  eventSlug?: string;
}

export interface EmailDeliveryReport {
  trackingStartAt: string;
  filters: { eventId: string | null };
  kpis: EmailDeliveryKpis;
  perEvent: EmailDeliveryPerEvent[];
  missing: {
    items: EmailDeliveryMissingItem[];
    pagination: { page: number; limit: number; total: number; pages: number };
  };
}

export const emailDeliveryReportService = {
  get: async (params: {
    eventId?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<EmailDeliveryReport>> => {
    const response = await api.get('/admin/reports/email-delivery', { params });
    return response.data;
  },

  /** Reenvia o email original via página pública de suporte (mesmo endpoint já existente). */
  resendByEmail: async (params: {
    email: string;
    eventSlug: string;
  }): Promise<{ success: boolean; message?: string }> => {
    const response = await api.post('/suporte-email/resend-email', params);
    return response.data;
  },
};

export interface PaymentThroughputKpis {
  totalAttempts: number;
  confirmed: number;
  alreadyConfirmed: number;
  errors: number;
  confirmFailed: number;
  notFound: number;
  avgPerHour: number;
  peakConfirmedPerMinute: number;
  p50DurationMs: number;
  p95DurationMs: number;
  errorRatePct: number;
}

export interface PaymentThroughputPerProvider {
  provider: string;
  attempts: number;
  confirmed: number;
  p50DurationMs: number;
  p95DurationMs: number;
  errorRatePct: number;
}

export interface PaymentThroughputPerMinute {
  minute: string;
  confirmed: number;
  attempts: number;
}

export interface PaymentThroughputSlowest {
  finishedAt: string;
  provider: string;
  registrationId?: string;
  eventTitle?: string;
  durationTotalMs: number;
  durationPspMs?: number;
  durationConfirmMs?: number;
  outcome: string;
}

export interface PaymentThroughputReport {
  trackingStartAt: string;
  window: { from: string; to: string; hours: number };
  filters: { eventId: string | null };
  kpis: PaymentThroughputKpis;
  perProvider: PaymentThroughputPerProvider[];
  perMinute: PaymentThroughputPerMinute[];
  slowest: PaymentThroughputSlowest[];
}

export const paymentThroughputReportService = {
  get: async (params: {
    hours?: number;
    eventId?: string;
  } = {}): Promise<ApiResponse<PaymentThroughputReport>> => {
    const response = await api.get('/admin/reports/payment-throughput', { params });
    return response.data;
  },
};

// Audit services
export const auditService = {
  getLogs: async (filters: any = {}): Promise<ApiResponse<{ logs: AuditLog[]; pagination: any }>> => {
    const response = await api.get('/admin/audit/logs', { params: filters });
    return response.data;
  },

  getLogById: async (id: string): Promise<ApiResponse<AuditLog>> => {
    const response = await api.get(`/admin/audit/logs/${id}`);
    return response.data;
  },

  getStats: async (filters: any = {}): Promise<ApiResponse<any>> => {
    const response = await api.get('/admin/audit/logs/stats', { params: filters });
    return response.data;
  },

  getUserActivity: async (userId: string, filters: any = {}): Promise<ApiResponse<any>> => {
    const response = await api.get(`/admin/audit/users/${userId}`, { params: filters });
    return response.data;
  },

  export: async (filters: any = {}, format: string = 'json'): Promise<any> => {
    const response = await api.get('/admin/audit/export', { 
      params: { ...filters, format },
      responseType: format === 'json' ? 'json' : 'blob'
    });
    return response.data;
  },
};

// Invoice services
export const invoiceService = {
  getEventsForInvoice: async (filters: any = {}): Promise<ApiResponse<{
    summary?: {
      valorTotal: number;
      valorPlataforma: number;
      valorParoquia: number;
      totalPix: number;
      totalCartao: number;
      totalOutros: number;
    };
    events: any[];
    pagination: any;
  }>> => {
    const response = await api.get('/admin/invoices/events', { params: filters });
    return response.data;
  },

  markInvoiceAsPaid: async (eventId: string, data: { paymentDate?: string; paymentMethod?: string; notes?: string }): Promise<ApiResponse<any>> => {
    const response = await api.put(`/admin/invoices/events/${eventId}/mark-paid`, data);
    return response.data;
  },

  registerInvoiceIssuance: async (eventId: string, data: { invoiceNumber?: string; invoiceDate?: string; notes?: string; recipientEmails?: string[] }): Promise<ApiResponse<any>> => {
    const response = await api.post(`/admin/invoices/events/${eventId}/register`, data);
    return response.data;
  },

  emitPlatformFeeNfse: async (eventId: string, data: Record<string, unknown>): Promise<ApiResponse<any>> => {
    const response = await api.post(`/admin/invoices/events/${eventId}/emit-nfse`, data);
    return response.data;
  },

  refreshPlatformFeeNfse: async (eventId: string): Promise<ApiResponse<any>> => {
    const response = await api.post(`/admin/invoices/events/${eventId}/nfse-status`);
    return response.data;
  },

  downloadPlatformFeeDanfe: async (eventId: string): Promise<Blob> => {
    const response = await api.get(`/admin/invoices/events/${eventId}/nfse-danfe`, { responseType: 'blob' });
    return response.data;
  },

  requestInvoiceData: async (eventId: string): Promise<ApiResponse<any>> => {
    const response = await api.post('/admin/invoices/request-data', { eventId });
    return response.data;
  },

  fillInvoiceDataManual: async (eventId: string, cpfCnpj: string): Promise<ApiResponse<any>> => {
    const response = await api.post('/admin/invoices/fill-data-manual', { eventId, cpfCnpj });
    return response.data;
  },

  backfillPaymentMethod: async (slug: string): Promise<ApiResponse<{ updated: number; failed: number; total: number; eventTitle?: string }>> => {
    const response = await api.post('/admin/invoices/backfill-payment-method', { slug });
    return response.data;
  },
};

export type ContadorAdmin = {
  _id: string;
  name: string;
  email: string;
  role: string;
  permissions?: string[];
  active: boolean;
  lastLogin?: string;
  createdAt?: string;
  updatedAt?: string;
};

export const contadorAccessService = {
  list: async (): Promise<ApiResponse<ContadorAdmin[]>> => {
    const response = await api.get('/admin/contadores');
    return response.data;
  },

  create: async (data: { name: string; email: string; password: string }): Promise<ApiResponse<ContadorAdmin>> => {
    const response = await api.post('/admin/contadores', data);
    return response.data;
  },

  update: async (
    id: string,
    data: { name?: string; password?: string; active?: boolean }
  ): Promise<ApiResponse<ContadorAdmin>> => {
    const response = await api.put(`/admin/contadores/${id}`, data);
    return response.data;
  },

  remove: async (id: string): Promise<ApiResponse<ContadorAdmin>> => {
    const response = await api.delete(`/admin/contadores/${id}`);
    return response.data;
  },
};

export type CustomPageInvite = {
  id: string;
  title: string;
  notes?: string | null;
  inviteeEmail?: string | null;
  inviteeName?: string | null;
  suggestedDomain?: string | null;
  branding?: {
    headline?: string;
    description?: string;
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
    organizationHint?: string;
  };
  enableCustomDomain: boolean;
  status: 'active' | 'used' | 'revoked' | 'expired';
  expiresAt?: string;
  usedAt?: string | null;
  usedParishId?: string | null;
  createdAt?: string;
  url?: string | null;
};

export const customPageInviteService = {
  list: async (): Promise<ApiResponse<CustomPageInvite[]>> => {
    const response = await api.get('/admin/custom-page-invites');
    return response.data;
  },
  create: async (data: {
    title: string;
    notes?: string;
    inviteeEmail?: string;
    inviteeName?: string;
    suggestedDomain?: string;
    enableCustomDomain?: boolean;
    expiresInDays?: number;
    branding?: CustomPageInvite['branding'];
  }): Promise<ApiResponse<CustomPageInvite>> => {
    const response = await api.post('/admin/custom-page-invites', data);
    return response.data;
  },
  revoke: async (id: string): Promise<ApiResponse<CustomPageInvite>> => {
    const response = await api.post(`/admin/custom-page-invites/${id}/revoke`);
    return response.data;
  },
  regenerate: async (id: string): Promise<ApiResponse<CustomPageInvite>> => {
    const response = await api.post(`/admin/custom-page-invites/${id}/regenerate`);
    return response.data;
  },
};

export type AdminCustomDomain = {
  id: string;
  domain: string;
  targetType?: 'event' | 'landing';
  eventId?: string | null;
  parishId: string;
  status: 'pending' | 'active' | 'disabled';
  verifiedAt?: string | null;
  lastCheckedAt?: string | null;
  lastError?: string | null;
  publicUrl?: string | null;
  dns?: {
    type: 'A' | 'CNAME';
    host: string;
    value: string;
    instructions: string;
    alternatives?: Array<{ type: string; value: string }>;
  };
  eventTitle?: string | null;
  eventSlug?: string | null;
  eventStatus?: string | null;
  parishName?: string | null;
  parishSlug?: string | null;
  customDomainPagesEnabled?: boolean;
};

export const adminCustomDomainService = {
  list: async (): Promise<ApiResponse<AdminCustomDomain[]>> => {
    const response = await api.get('/admin/custom-domains');
    return response.data;
  },
  upsert: async (data: {
    domain: string;
    targetType?: 'event' | 'landing';
    eventId?: string;
    parishId?: string;
  }): Promise<ApiResponse<AdminCustomDomain>> => {
    const response = await api.put('/admin/custom-domains', data);
    return response.data;
  },
  verify: async (id: string): Promise<ApiResponse<AdminCustomDomain>> => {
    const response = await api.post(`/admin/custom-domains/${id}/verify`);
    return response.data;
  },
  disable: async (id: string): Promise<ApiResponse<AdminCustomDomain>> => {
    const response = await api.post(`/admin/custom-domains/${id}/disable`);
    return response.data;
  },
};

export type PersonalizedParish = {
  id: string;
  name: string;
  slug?: string;
  city?: string;
  state?: string;
  email?: string;
  status?: string;
  customDomainPagesEnabled: boolean;
  landingPublished?: boolean;
};

export const personalizedParishService = {
  list: async (): Promise<ApiResponse<PersonalizedParish[]>> => {
    const response = await api.get('/admin/personalized-parishes');
    return response.data;
  },
  setEnabled: async (parishId: string, enabled: boolean): Promise<ApiResponse<PersonalizedParish>> => {
    const response = await api.post(`/admin/parishes/${parishId}/custom-pages`, { enabled });
    return response.data;
  },
  publishLanding: async (
    parishId: string
  ): Promise<ApiResponse<{ parishSlug?: string; previewPath?: string; eventCount?: number }>> => {
    const response = await api.post(`/admin/parishes/${parishId}/landing/publish`);
    return response.data;
  },
};

// App Banners / Campanhas (para edição no App para App)
export interface AppBanner {
  _id: string;
  title: string;
  imageUrl: string;
  link?: string;
  order: number;
  active: boolean;
  startDate: string;
  endDate: string;
  companyName: string;
  contact: string;
  createdAt?: string;
  updatedAt?: string;
}

export type AppBannerPayload = {
  title: string;
  imageUrl?: string;
  link?: string;
  order?: number;
  active?: boolean;
  startDate: string;
  endDate: string;
  companyName: string;
  contact: string;
};

function buildBannerFormData(payload: AppBannerPayload, imageFile?: File | null): FormData {
  const fd = new FormData();
  fd.append('title', payload.title);
  fd.append('startDate', payload.startDate);
  fd.append('endDate', payload.endDate);
  fd.append('companyName', payload.companyName);
  fd.append('contact', payload.contact);
  if (payload.link != null) fd.append('link', payload.link);
  if (payload.order != null) fd.append('order', String(payload.order));
  fd.append('active', payload.active !== false ? 'true' : 'false');
  if (payload.imageUrl && !imageFile) fd.append('imageUrl', payload.imageUrl);
  if (imageFile) fd.append('image', imageFile);
  return fd;
}

export const appBannerService = {
  list: async (): Promise<ApiResponse<AppBanner[]>> => {
    const response = await api.get('/admin/app-banners');
    return response.data;
  },
  create: async (payload: AppBannerPayload, imageFile?: File | null): Promise<ApiResponse<AppBanner>> => {
    const formData = buildBannerFormData(payload, imageFile);
    const response = await api.post('/admin/app-banners', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  update: async (id: string, payload: AppBannerPayload, imageFile?: File | null): Promise<ApiResponse<AppBanner>> => {
    const formData = buildBannerFormData(payload, imageFile);
    const response = await api.put(`/admin/app-banners/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  remove: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/admin/app-banners/${id}`);
    return response.data;
  },
  reorder: async (ids: string[]): Promise<ApiResponse<AppBanner[]>> => {
    const response = await api.put('/admin/app-banners/reorder', { ids });
    return response.data;
  },

  getHighlightedEventsOrder: async (): Promise<ApiResponse<{ eventIds: string[] }>> => {
    const response = await api.get('/admin/highlighted-events/order');
    return response.data;
  },

  updateHighlightedEventsOrder: async (eventIds: string[]): Promise<ApiResponse<{ eventIds: string[] }>> => {
    const response = await api.put('/admin/highlighted-events/order', { eventIds });
    return response.data;
  },

  getAnalytics: async (params?: { bannerId?: string; days?: number }): Promise<ApiResponse<{
    totalImpressions: number;
    totalClicks: number;
    ctr: number;
    byCampaign: Array<{
      bannerId: string;
      title: string;
      companyName: string;
      totalImpressions: number;
      totalClicks: number;
      ctr: number;
      byDay: Array<{ date: string; impressions: number; clicks: number }>;
    }>;
    period: { startDate: string; days: number };
  }>> => {
    const response = await api.get('/admin/app-banners/analytics', { params });
    return response.data;
  },
};

export interface PaymentQueuesOverview {
  generatedAt: string;
  windowHours: number;
  windowFrom?: string;
  windowTo?: string;
  redis: {
    available?: boolean;
    queueEnabled?: boolean;
    streamKey?: string;
    length?: number;
    pendingInGroup?: number;
    error?: string;
  };
  worker: {
    reachable: boolean;
    statsUrl: string;
    dashboardUrl: string;
    stats: Record<string, unknown> | null;
  };
  emailQueue: {
    name: string;
    waiting?: number;
    active?: number;
    completed?: number;
    failed?: number;
    delayed?: number;
    total?: number;
    error?: string;
  };
  streamEvents: Array<{
    id: string;
    occurredAt: string;
    eventType: string;
    provider?: string;
    providerPaymentId?: string;
    registrationId?: string | null;
    correlationId?: string;
  }>;
  registrations: Array<{
    id: string;
    confirmationCode: string;
    name: string;
    email: string;
    eventTitle: string;
    paymentStatus: string;
    paymentMethod: string;
    paymentId: string;
    amountPaid: number;
    updatedAt: string;
    createdAt: string;
    paymentProvider: 'mercadopago' | 'pagarme' | null;
    paymentProviderLabel: string;
    splitApplied: boolean | null;
    splitLabel: string;
    splitDetail: string | null;
  }>;
  failedRegistrations: Array<{
    id: string;
    confirmationCode: string;
    name: string;
    email: string;
    eventTitle: string;
    paymentStatus: string;
    paymentMethod: string;
    paymentId: string;
    amountPaid: number;
    updatedAt: string;
    createdAt: string;
    paymentProvider: 'mercadopago' | 'pagarme' | null;
    paymentProviderLabel: string;
    splitApplied: boolean | null;
    splitLabel: string;
    splitDetail: string | null;
  }>;
  summary: {
    streamEventsCount: number;
    registrationsCount: number;
    failedRegistrationsCount: number;
    byPaymentStatus: Record<string, number>;
  };
}

export interface ManualReconcileResult {
  success: boolean;
  outcome: string;
  message: string;
  registrationId: string;
  pspStatus?: string;
  paymentStatus?: string;
  email?: string;
  emailSkipped?: boolean;
}

export const paymentQueueMonitorService = {
  getOverview: async (params?: {
    hours?: number;
    from?: string;
    to?: string;
  }): Promise<ApiResponse<PaymentQueuesOverview>> => {
    const response = await api.get('/admin/payment-queues/overview', {
      params: {
        hours: params?.hours ?? 24,
        from: params?.from,
        to: params?.to,
      },
    });
    return response.data;
  },

  reconcileRegistration: async (
    registrationId: string
  ): Promise<ApiResponse<ManualReconcileResult>> => {
    const response = await api.post(
      `/admin/payment-queues/registrations/${registrationId}/reconcile`
    );
    return response.data;
  },
};

export type ProducerSegment = 'ativo' | 'parado' | 'nunca';
export type ProducerNeverKind = 'nenhum' | 'so_rascunho' | null;

export interface ProducerCrmRow {
  id: string;
  name: string;
  city: string;
  state: string;
  email: string;
  phone: string;
  status: string;
  createdAt: string;
  daysSinceSignup: number;
  adminName: string;
  adminEmail: string;
  segment: ProducerSegment;
  neverKind: ProducerNeverKind;
  realizedCount: number;
  upcomingCount: number;
  draftCount: number;
  lastEventTitle: string | null;
  lastEventEnd: string | null;
  daysSinceLastEvent: number | null;
  nextEventTitle: string | null;
  nextEventStart: string | null;
}

export interface ProducerCrmData {
  idleDays: number;
  generatedAt: string;
  summary: {
    total: number;
    jaRealizaram: number;
    ativos: number;
    parados: number;
    nunca: number;
    nuncaSemCadastro: number;
    nuncaSoRascunho: number;
    comProximo: number;
  };
  producers: ProducerCrmRow[];
}

export interface MarketingEmailDraft {
  subject: string;
  preheader: string;
  headline: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
}

export interface MarketingEmailPreview {
  valid: boolean;
  message: string | null;
  subject: string;
  html: string;
}

export interface MarketingEmailSendResult {
  sent: number;
  failed: number;
  skippedNoEmail: number;
  testTo?: string;
  failures?: Array<{ email: string; message: string }>;
}

export const producerCrmService = {
  get: async (idleDays = 180): Promise<ApiResponse<ProducerCrmData>> => {
    const response = await api.get('/admin/crm/producers', { params: { idleDays } });
    return response.data;
  },

  previewMarketingEmail: async (
    payload: MarketingEmailDraft & { sampleNome?: string; sampleParoquia?: string }
  ): Promise<ApiResponse<MarketingEmailPreview>> => {
    const response = await api.post('/admin/crm/marketing-email/preview', payload);
    return response.data;
  },

  sendMarketingEmail: async (
    payload: MarketingEmailDraft & {
      mode: 'test' | 'producers';
      parishIds?: string[];
      sampleNome?: string;
      sampleParoquia?: string;
    }
  ): Promise<ApiResponse<MarketingEmailSendResult>> => {
    const response = await api.post('/admin/crm/marketing-email/send', payload);
    return response.data;
  },

  generateMarketingEmail: async (prompt: string): Promise<ApiResponse<MarketingEmailDraft>> => {
    const response = await api.post('/admin/crm/marketing-email/generate', { prompt });
    return response.data;
  },
};

